import React, { createContext, useContext, useState, useCallback } from "react";
import { AuthContext } from "../components/auth/auth-provider";

// Create the context
export const CarriereEventsContext = createContext(null);

// Hook to use the context
export const useCarriereEvents = () => {
  const context = useContext(CarriereEventsContext);
  if (!context) {
    throw new Error("useCarriereEvents must be used within a CarriereEventsProvider");
  }
  return context;
};

// Event to message mapping
const EVENT_MESSAGES = {
  RIS: {
    MODAL_OPEN: `Pour commencer, assurez-vous d'avoir le <b>relevé tous régimes officiel</b> téléchargé depuis le site de l'Assurance retraite.\n\nLes scans ou fichiers convertis ne fonctionnent pas.\n\nCliquez sur le bouton bleu <b>« Importer un fichier »</b>.`,
    FILE_UPLOADED: `<b>Bravo, l'import du relevé de carrière est réussi.</b>\nVous pouvez maintenant consulter et éditer <b>l'analyse de carrière</b> afin d'identifier d'éventuelles anomalies.\n\nIl est possible que le simulateur vous demande des <b>précisions complémentaires</b> à la suite de l'import. Je vous invite vivement à les examiner en cliquant sur le bouton suivant et à <b>bien lire les points d'information associés.</b>\n\nUne fois ces vérifications effectuées, vous pourrez cliquer sur le bouton <b>« Valider »</b> pour finaliser l'import.`,
    FILE_UPLOAD_ERROR: `<b>Erreur lors de l'import du fichier.</b>\n\nVeuillez vérifier que le fichier est bien un relevé de carrière officiel téléchargé depuis le site de l'Assurance retraite.\n\nLes scans ou fichiers convertis ne sont pas acceptés. Merci de réessayer avec un fichier valide.`,
    PRECISIONS_TAB: "Section Précisions : Corrigez les ambiguïtés détectées dans les champs ci-dessous.",
  },
  PERIODE: {
    MODAL_OPEN: `La première étape consiste à renseigner les informations relatives la période d'activité professionnelle.

<b>📅 Date de début</b>
Indiquez la date à laquelle commence votre période d'activité professionnelle.

<b>⏱️ Fin de période</b>
Deux options s'offrent à vous :

• <b>Départ en retraite</b>
La période est automatiquement projetée jusqu'à la date de départ en retraite définie dans le paramétrage de la retraite (par défaut : l'âge légal).
👉 <i>Ce choix est idéal si vous souhaitez mesurer l'impact d'un départ différé à la retraite sur 5 ans.</i>

• <b>Personnalisé</b>
Vous définissez vous-même la date de fin de la période.
👉 <i>Cette option est utile si l'assuré arrête son activité avant son départ en retraite (chômage, inactivité, autre projet, etc.).</i>

<b>ℹ️ Important</b>
Si vous souhaitez modifier la date de départ en retraite, cela doit se faire dans le paramétrage de la retraite, et non en modifiant la date de fin personnalisée.
Dans le cas contraire, il n'y aura plus de projection sur 5 ans.

<b>💼 Activité</b>
Sélectionnez le type d'activité professionnelle exercée pendant cette période (salarié, TNS, profession libérale, etc.) ou des périodes spécifiques (maladie, maternité, invalidité…).

Une fois ces informations renseignées, cliquez sur <b>« Suivant »</b> pour passer à l'étape des revenus.`,

    TAB_REVENUS: `Section Revenus : Saisissez les revenus de la période professionnelle.

<b>💰 Revenus soumis à cotisations sociales</b>
Pour chaque année, indiquez les revenus professionnels qui ont été soumis aux cotisations sociales :

• <b>Salariés et assimilés :</b> Salaire brut
• <b>TNS (Travailleurs Non Salariés) :</b> Assiette spécifique (rémunération imposable, cotisations facultatives Madelin, dividendes soumis aux cotisations sociales, etc.)
• <b>Micro-entrepreneurs :</b> Chiffre d'affaires annuel

<b>📈 Taux d'indexation</b>
Permet d'appliquer une évolution automatique des revenus d'une année sur l'autre. Par exemple, un taux de 2% augmente les revenus de 2% chaque année.

<b>🧮 Forcer les revenus au PASS :</b> 
Ce bouton permet de plafonner automatiquement tous les revenus au Plafond Annuel de la Sécurité Sociale (PASS) pour chaque année. Utile pour les périodes où les revenus dépassaient systématiquement le PASS.

<b>📊 Trimestres</b>
Le nombre de trimestres validés pour chaque année est calculé automatiquement en fonction des revenus saisis. Vous pouvez toutefois les modifier manuellement si nécessaire en cochant la case associée.

<b>⚡ Déplafonnement du PASS</b>
Cochez cette option pour les années où l'assuré a exercé une activité en multi-employeur, notamment lorsque les revenus cumulés des différents employeurs dépassent le plafond annuel de la Sécurité Sociale (PASS).

Une fois les revenus renseignés, cliquez sur <b>« Suivant »</b> pour accéder aux paramètres de la période.`,
    TAB_PARAMETRES: `Section Paramètres : Configurez les paramètres spécifiques selon le régime de retraite.

<b>ℹ️ Information importante :</b> Les paramètres disponibles varient automatiquement selon le type d'activité sélectionné (salarié, TNS, profession libérale, fonction publique...).

<b>Paramètres principaux selon les régimes :</b>

<b>👔 Pour les salariés :</b>
• <b>Taux d'activité :</b> Représente votre temps de travail par rapport au temps plein.
  → <b>100%</b> = Temps plein 
  → <b>80%</b> = 4/5ème temps (28h/semaine)
  → <b>50%</b> = Mi-temps 
  <i>Ce taux influence directement la proratisation du PASS.</i>
• <b>💳 Cotisations AGIRC-ARRCO :</b> Taux de cotisations salariales et patronales sur Tranche 1 et Tranche 2
• <b>Affiliation AGIRC-ARRCO (CFE) :</b> Précisez si le salarié détaché à l'étranger cotise à l'AGIRC-ARRCO

<b>🏛️ Pour la fonction publique :</b>
• <b>Catégorie :</b> Sédentaire ou Active (impacte l'âge de départ)
• <b>Limite d'âge personnalisée :</b> Âge maximal de départ en retraite
• <b>Bonifications :</b> Trimestres de majoration (hors enfants)
• <b>Indice majoré :</b> Indice de rémunération de fin de carrière
• <b>Assiette RAFP :</b> Base de calcul pour le régime additionnel (en % ou forfaitaire)

<b>💼 Pour les professions libérales :</b>
• <b>Classe de retraite :</b> Classe de cotisation selon la caisse (CARPV, CNBF, CAVEC, CRN, etc.)
• <b>Conventionné :</b> Statut conventionné pour certaines professions de santé (CARMF, CARCDSF, CARPIMKO, CAVP)
• <b>Taux de prise en charge CPAM :</b> Pour les professionnels de santé conventionnés

<b>🔧 Pour les TNS (Travailleurs Non Salariés) :</b>
• <b>Affiliation RAVGDT :</b> Pour les agents généraux d'assurance (CAVAMAC)
• <b>Commissions brutes :</b> Pour le calcul des droits CAVAMAC

<b>📋 Pour les trimestres spécifiques :</b>
• <b>Validation :</b> Taux de pension seul ou taux + durée d'assurance
• <b>Nature :</b> Trimestres assimilés ou cotisés

<b>🏥 Pour les périodes de chômage/maladie/invalidité :</b>
• <b>Affiliation AGIRC-ARRCO :</b> Si la période ouvre droit à des points de retraite complémentaire
• <b>Moyenne journalière des points :</b> Pour le calcul des droits en cas de maladie/invalidité

<b>💡 Conseil :</b> Les paramètres par défaut sont généralement adaptés aux situations les plus courantes. Ne les modifiez que si vous disposez d'informations précises sur la situation de votre client.`,
    VALIDATE: "Période ajoutée avec succès !",
  },
  PARAM: {
    MODAL_OPEN: "Paramètres de retraite : Configurez les paramètres généraux de calcul de la retraite.",
    TAB_DATE_DEPART: `
    📅 <b>Date de départ à la retraite</b>
Par défaut, l’âge est positionné sur l’âge légal. Je vous conseille de le laisser ainsi : l’étude intègre automatiquement une projection sur 5 ans et permet d’identifier la date de départ au taux plein.

⏱️ <b>Carrière longue</b>
Positionner un départ au taux plein permet  de vérifier si votre client peut bénéficier du dispositif  "carrière longue".

<b>🔓 Déblocage de l'ouverture des droits</b>
⚠️ Sauf cas très spécifiques nécessitant une expertise particulière (départ anticipé pour handicap ou incapacité permanente), je vous recommande de ne pas cocher la case de déblocage de l’ouverture des droits. Celà pourrait conduire à afficher une pension à un âge auquel l'assuré n'y a pas droit.`,
    TAB_POINTS: `
    🔢 <b>Points de retraite</b>

Dans 99 % des cas, les points sont correctement importés. Je vous invite néanmoins à les contrôler afin de confirmer qu’ils correspondent bien à la carrière de votre client.

Si ce n’est pas le cas, vous pouvez corriger directement les informations dans les champs concernés.

<b>💡 À noter</b> : les éventuels points AGIRC acquis avant 2016 peuvent apparaître avant conversion.
Pas d’inquiétude, le logiciel effectue automatiquement la conversion nécessaire pour le calcul de la pension.`,
    TAB_MAJORATIONS: `
    👶 <b>Majorations pour enfants</b>

Par défaut, les majorations pour enfants sont affectées au régime général.

Toutefois, lorsque votre client a cotisé à la fois au régime général et à un régime spécial (fonction publique ou CRPCEN), ce sont en principe ces régimes spéciaux qui accordent les majorations de trimestres, si les conditions sont remplies.

Si nécessaire, vous pouvez donc ajuster manuellement la répartition des majorations entre les différents régimes afin de refléter au mieux la situation réelle de votre client.`,
    TAB_TAUX_PLEIN: `✅ <b>Forcer le taux plein</b>

<b>🎯 Objectif :</b> Simuler des situations particulières permettant d'obtenir le taux plein avant ou à compter de l'âge légal.

<b>♿ Exemples :</b>
• Départ en retraite pour inaptitude
• Parent d'un enfant handicapé

<b>⚠️ Important :</b> Le logiciel ne détecte pas automatiquement ces situations. Vous devez les activer manuellement si votre client est concerné.`,
    TAB_IMPORT_RG: `
    📄 <b>Import du relevé Régime Général</b>

L’import du relevé du régime général permet d’aller un peu plus loin dans l’analyse. Il sert à corriger les revenus issus du relevé tous régimes, lorsque ceux-ci ne correspondent pas exactement aux revenus réellement cotisés par votre client.

En effet, les revenus pris en compte au régime général sont plafonnés au PASS et peuvent être proratisés dans certaines situations :
• période d’activité incomplète (pas du 1er janvier au 31 décembre),
• activité exercée à temps partiel.

Ces ajustements peuvent avoir une incidence directe sur le calcul du revenu annuel moyen (RAM), d’où l’intérêt de cet import pour affiner l’étude.

<b>💡 À noter</b> : ce document est téléchargeable uniquement si l’assuré a plus de 55 ans.
Dans le cas contraire, il est important de prévenir le client qu’une marge d’erreur peut subsister dans le calcul du RAM.`,
    TAB_TAUX_REMPLACEMENT: `
    📊 <b>Taux de remplacement</b>

<b>🎯 Objectif :</b> Les revenus nets de l'année en cours permettent d'établir le taux de remplacement à la retraite. Le logiciel effectue un ratio entre ces revenus et la pension nette.

<b>💡 Info :</b> Par défaut, le logiciel reprend le montant renseigné dans la période pour l'année en cours. S'agissant des salariés du secteur privé, le logiciel applique un taux de charges salariales de 23%.`,
    TAB_PRELEVEMENTS: `
💶 <b>Prélèvements sociaux sur les pensions</b>

<b>🎯 Objectif :</b> Paramétrer les prélèvements sociaux appliqués sur les pensions de retraite.

<b>Types de prélèvements :</b>

<b>1️⃣ CSG (Contribution Sociale Généralisée) :</b>
• <b>Taux normal :</b> 8,3% (dont 5,9% déductibles)
• <b>Taux réduit :</b> 6,6% (dont 4,2% déductibles)
• <b>Taux médian :</b> 3,8% (entièrement déductible)
• <b>Exonération :</b> Possible selon le RFR

<b>2️⃣ CRDS :</b> 0,5% (non déductible)

<b>3️⃣ CASA :</b> 0,3% (pour les non exonérés de CSG)

<b>🎚️ Détermination :</b>
Le taux dépend du revenu fiscal de référence (RFR) du foyer fiscal.`,
    TAB_CONTRATS: `
    📋 <b>Contrats d'épargne retraite personnels</b>

<b>🎯 Objectif :</b> Intégrer les contrats d'épargne retraite souscrits à titre personnel ou professionnel.

<b>Types de contrats :</b>

<b>1️⃣ PER (Plan d'Épargne Retraite) :</b>
• PER individuel
• PER collectif (PERCO/PERECO)
• PER obligatoire (PERO)

<b>2️⃣ Anciens contrats :</b>
• <b>PERP :</b> Plan d'Épargne Retraite Populaire
• <b>Madelin :</b> Pour les TNS
• <b>Article 83 :</b> Contrat collectif d'entreprise
• <b>PEE :</b> Plan d'Épargne Entreprise
• <b>Assurance-vie :</b> Avec sortie en rente

<b>📊 Informations à renseigner :</b>
• Capital constitué actuel
• Versements futurs et périodicité
• Taux de rendement prévu
• Mode de sortie (capital, rente ou mixte)
• Taux de conversion pour les rentes`,
    TAB_AUTRES_PENSIONS: `
    💰 <b>Autres pensions</b>

<b>🎯 Objectif :</b> Intégrer les pensions de retraite perçues au titre de régimes non intégrés dans l'étude ou de régimes étrangers.

<b>Types de pensions concernées :</b>

<b>🌍 Régimes étrangers :</b>
• Pensions de retraite étrangères (hors UE)
• Pensions de régimes internationaux
• Pensions de régimes d'expatriation

<b>🏛️ Régimes spéciaux non intégrés :</b>
• Régimes de la RATP, SNCF, Banque de France
• Régimes des industries électriques et gazières (IEG)
• Autres régimes spéciaux non couverts par le simulateur

<b>📊 Informations à renseigner :</b>
• Libellé de la pension
• Prélevements sociaux appliquables
• Pension brute

<b>💡 Conseil :</b> Ces pensions seront intégrées dans le calcul global des revenus à la retraite et prises en compte pour l'analyse du taux de remplacement.`,
    TAB_REGIMES_LIQUIDABLES: "Régimes liquidables : Sélectionnez les régimes à liquider.",
    VALIDATE: "Paramètres enregistrés avec succès !",
  },
  BIENVENUE: {
    INIT: `Bonjour {prenom},\n\nJe suis Alan, votre assistant virtuel pour vous accompagner dans l'utilisation de MAKO.\n\nChoisissez une catégorie pour obtenir de l'aide :`,
    CATEGORIES: [
      {
        id: "RELEVE_IMPORT",
        title: "Relevé tous régimes et import des données",
        description: "Tout ce qu'il faut savoir sur le téléchargement, l'import et le contrôle du relevé de carrière tous régimes.",
        questions: [
          { id: "Q1", text: "Comment récupérer le bon document ?" },
          { id: "Q2", text: "Comment l'intégrer dans MAKO ?" },
          { id: "Q3", text: "Est-ce que le logiciel recule les trimestres en fonction des revenus ?" },
          { id: "Q4", text: "Pourquoi l'import n'a pas fonctionné et quoi faire ?" },
          { id: "Q5", text: "Pourquoi toutes les périodes n'ont pas été importées ?" },
          { id: "Q6", text: "Ajouter une période non indiquée sur le RIS" },
          { id: "Q7", text: "Le relevé ne mentionne pas les revenus sur les professions libérales" },
          { id: "Q8", text: "Comment contrôler la fiabilité de l'import ?" },
          { id: "Q9", text: "A quoi sert l'import du relevé du régime général ?" },
        ],
      },
      {
        id: "CARRIERE_PROJECTION",
        title: "Carrière et projection",
        description: "Paramétrez l'analyse de carrière et projetez les droits futurs en fonction de la situation professionnelle de votre client.",
        questions: [
          { id: "Q10", text: "Comment ajouter la carrière actuelle et future ?" },
          { id: "Q11", text: "Mon client cumule deux activités" },
          { id: "Q12", text: "Paramétrer l'âge de départ en retraite" },
          { id: "Q13", text: "Mon client a droit à la carrière longue" },
          { id: "Q14", text: "Comment sont intégrées les majorations enfants" },
          { id: "Q15", text: "Mon client a droit a un départ anticipé pour inaptitude" },
        ],
      },
      {
        id: "CALCUL_DROITS",
        title: "Calcul des droits et trimestres",
        description: "Comprenez les mécanismes de calcul des trimestres, des majorations et des pensions sur les différents régimes.",
        questions: [
          { id: "Q16", text: "Comment obtenir le détail des calculs?" },
          { id: "Q17", text: "Savoir lire le tableau du RAM" },
          { id: "Q18", text: "Le logiciel ne calcule pas la majoration enfants sur le régime AGIRC-ARRCO ?" },
          { id: "Q19", text: "Est-ce que le logiciel calcule la minoration spécifique sur les points en TC ?" },
          { id: "Q20", text: "Pour quoi les trimestres ne bougent pas sur la projection sur 5 ans" },
          { id: "Q21", text: "Pourquoi le logiciel valide plus de 4 trimestres par an dans le cadre de projection sur 5 ans ?" },
        ],
      },
      {
        id: "CONSEILS",
        title: "Conseils",
        description: "Conseils pratiques pour la liquidation des retraites et la validation de la cohérence de votre étude.",
        questions: [
          { id: "Q22", text: "Liquidation d'une profession libérale" },
          { id: "Q23", text: "Ajouter un contrat d'épargne retraite" },
          { id: "Q24", text: "Ajouter une carrière à l'étranger" },
        ],
      },
    ],
  },
  QUESTIONS: {
    // Relevé tous régimes et import des données
    Q1: `Vous pouvez télécharger le RIS directement depuis le site <b>info-retraite.fr</b>.\n\nPour vous aider, une <b><a href="https://api-v3.mako-box.com/ApiMako/V3/Documents/Procédure d'import.pdf" target="_blank" rel="noopener noreferrer">procédure pas à pas</a></b> est mise à votre disposition.`,
    Q2: `Pour intégrer le relevé dans MAKO :\n\n1. Cliquez sur le bouton violet <b>"Importer le RIS"</b>\n2. Dans la fenêtre qui s'ouvre, cliquez sur <b>"Importer un fichier"</b>\n3. Sélectionnez votre fichier PDF téléchargé depuis le site de l'Assurance retraite\n4. Patientez pendant l'analyse du document\n5. Vérifiez les données importées dans l'analyse de carrière\n\nSi des précisions sont demandées, prenez le temps de les renseigner pour garantir la fiabilité du calcul.`,
    Q3: `Non, le logiciel ne recalcule pas les trimestres à partir des revenus.\n\n<b>Fonctionnement :</b>\nLe logiciel reprend uniquement les trimestres tels qu'ils apparaissent dans le relevé de situation individuelle (RIS), sans les recalculer.\n\n<b>Pourquoi ce choix ?</b>\n• Les revenus figurant sur le RIS ne permettent pas, à eux seuls, de vérifier de manière fiable le nombre de trimestres validés\n• La durée réelle des périodes d'activité peut avoir un impact sur la validation des trimestres, et cette information est souvent imprécise sur les relevés\n• Certaines périodes permettent de valider des trimestres sans être directement liées aux revenus\n\n<b>Exception importante :</b>\nPour le régime des fonctionnaires, le logiciel calcule les trimestres en fonction de la durée des périodes, ce mode de calcul étant adapté à ce régime spécifique.`,
    Q4: `Si l'import n'a pas fonctionné, cela arrive le plus souvent dans les cas suivants :\n\n<b>Causes fréquentes :</b>\n• Le fichier importé est un scan du relevé de carrière\n• Le relevé est bien officiel, mais le fichier a été modifié ou converti par un logiciel tiers\n\n<b>Solution :</b>\nPour que l'import fonctionne correctement, utilisez le <b>relevé tous régimes officiel</b>, tel qu'il a été téléchargé depuis le site de l'Assurance retraite, sans modification.\n\nJe vous invite à reprendre la <b><a href="https://api-v3.mako-box.com/ApiMako/V3/Documents/Procédure d'import.pdf" target="_blank" rel="noopener noreferrer">procédure d'import</a></b>.\n\nSi le problème persiste, n'hésitez pas à nous transférer le relevé par mail pour vérification.`,
    Q5: `Si toutes les périodes n'ont pas été importées, cela peut s'expliquer par deux raisons principales :\n\n<b>1. Régimes non couverts</b>\nCertains régimes présents sur le RIS ne sont pas couverts par le logiciel, comme par exemple :\n• Le régime des artistes-auteurs\n• Certains régimes spécifiques (RATP, CNIEG, etc.)\n\n<b>2. Modification de la structure du RIS</b>\nIl arrive que les services Info-retraite modifient la structure du document RIS. Dans ce cas, l'interprétation automatique réalisée par le simulateur MAKO peut être temporairement perturbée.\n\n<b>Que faire ?</b>\nSi vous êtes dans cette situation, je vous recommande de transmettre le RIS à l'équipe support à l'adresse <b>support@mako-remexpert.com</b>, afin qu'elle puisse analyser le document et vous accompagner.`,
    Q6: `Pour ajouter une période non indiquée sur le RIS :\n\n1. Cliquez sur le bouton <b>"Ajouter une période"</b>\n2. Sélectionnez le type de période (salarié, TNS, fonction publique, etc.)\n3. Renseignez les dates de début et de fin\n4. Dans l'onglet <b>"Revenus"</b>, saisissez les revenus de la période\n5. Dans l'onglet <b>"Paramètres"</b>, configurez les options spécifiques\n6. Cliquez sur <b>"Valider"</b>\n\nLa période sera intégrée dans l'analyse de carrière et prise en compte dans le calcul.`,
    Q7: `C'est tout à fait normal si le relevé ne mentionne pas les revenus pour les professions libérales.\n\nCes régimes fonctionnent selon un système par points et non sur la base des revenus.\n\nLe logiciel n'a donc pas besoin de ces informations : il calcule les droits directement à partir des points acquis.`,
    Q8: `Dans 99 % des cas, l'import se déroule correctement.\n\nLe contrôle reste néanmoins essentiel afin de s'assurer que toutes les informations correspondent bien à la situation réelle de votre client.\n\nPour contrôler la fiabilité de l'import, il est recommandé de vérifier trois éléments clés de la carrière : les <b>trimestres</b>, les <b>revenus</b> et les <b>points</b>.\n\n<b>1. Vérifier les trimestres</b>\nSi le nombre de trimestres ne vous semble pas correct, pas de panique. Le plus simple est de les vérifier directement dans le tableau des trimestres.\n• Cliquez sur le bouton bleu <b>« Détails des calculs »</b>, situé en bas à droite de l'écran\n• Une fois la fenêtre de détails ouverte, cliquez dans le menu de gauche sur l'onglet <b>« Trimestres »</b>\n• Vous pourrez ainsi identifier le régime concerné et les années pour lesquelles des trimestres sont éventuellement manquants\n\n<b>2. Vérifier les revenus</b>\nLeur vérification n'est utile que pour le calcul du revenu annuel moyen au sein des régimes alignés. Le plus simple est donc d'aller directement dans le détail du RAM.\n• Cliquez sur le bouton bleu <b>« Détails des calculs »</b>, situé en bas à droite de l'écran\n• Une fois la fenêtre de détails ouverte, rendez-vous dans l'onglet <b>« RAM »</b>\n• Vous pourrez alors contrôler les revenus affichés dans la première colonne, année par année\n• Si vous souhaitez corriger des montants, retournez sur la carrière et ouvrez la ou les périodes concernées en cliquant sur l'icône vert à droite de la période\n\n<b>3. Vérifier les points</b>\nVous pouvez les contrôler en cliquant sur le bouton rouge <b>« Paramétrer la retraite »</b>.\n• Cliquez ensuite sur l'onglet <b>« Points »</b> dans le menu de gauche\n• Si le nombre de points sur un régime ne correspond pas à ce qu'indique le RIS, vous pouvez corriger directement les informations dans les champs concernés\n\n<b>À noter :</b> Les éventuels points AGIRC acquis avant 2016 peuvent apparaître avant conversion. Pas d'inquiétude, le logiciel effectue automatiquement la conversion nécessaire pour le calcul de la pension.`,
    Q9: `L'import du relevé du régime général permet d'aller un peu plus loin dans l'analyse.\n\nIl sert à corriger les revenus issus du relevé tous régimes, lorsque ceux-ci ne correspondent pas exactement aux revenus réellement cotisés par votre client.\n\n<b>Pourquoi cette correction peut être nécessaire ?</b>\nEn effet, les revenus pris en compte au régime général sont plafonnés au PASS et peuvent être proratisés dans certaines situations :\n• Période d'activité incomplète (pas du 1er janvier au 31 décembre)\n• Activité exercée à temps partiel\n\nCes ajustements peuvent avoir une incidence directe sur le calcul du revenu annuel moyen (RAM), d'où l'intérêt de cet import pour affiner l'étude.\n\n<b>À noter :</b> Ce document est téléchargeable uniquement si l'assuré a plus de 55 ans. Dans le cas contraire, il est important de prévenir le client qu'une marge d'erreur peut subsister dans le calcul du RAM.`,

    // Carrière et projection
    Q10: `Rien de plus simple, je vous invite à cliquer sur le bouton vert <b>« Ajouter une période ».</b>`,
    Q11: `Si votre client cumule plusieurs activités, ce n'est absolument pas un problème.\n\nIl suffit d'ajouter chaque période l'une après l'autre, en cliquant à nouveau sur le bouton vert <b>« Ajouter une période »</b> pour chacune d'elles.\n\nLe logiciel prendra automatiquement en compte l'ensemble des activités dans l'étude.`,
    Q12: `Par défaut, l'âge est positionné sur l'âge légal. Je vous conseille de le laisser ainsi : l'étude intègre automatiquement une projection sur 5 ans et permet d'identifier la date de départ au taux plein.\n\n<b>Modifier l'âge de départ :</b>\nSi vous souhaitez prévoir un scénario de départ différent, vous pouvez cliquer sur le bouton rouge <b>« Paramétrer la retraite »</b> puis dans le menu de gauche sur l'onglet <b>« Date de départ »</b>.\n\nVous avez la possibilité de créer différents scénarios, en définissant une date de départ ou un âge personnalisé, afin d'explorer plusieurs options.\n\n<b>Conseil :</b>\nIl peut être utile de positionner un départ au taux plein, notamment pour vérifier si votre client peut bénéficier d'un dispositif de carrière longue.\n\n<b>Attention :</b>\nSauf cas très spécifiques nécessitant une expertise particulière, je vous recommande de ne pas cocher la case de déblocage de l'ouverture des droits. La cocher pourrait conduire à indiquer que le client a droit à une pension à un âge qui ne serait pas le bon.`,
    Q13: `Pour vérifier si votre client est éligible à une carrière longue dans MAKO, il suffit de positionner l'âge de départ sur le taux plein. Le logiciel analysera alors automatiquement si les conditions sont remplies.\n\n<b>Procédure :</b>\nConcrètement, cliquez sur le bouton rouge <b>« Paramétrer la retraite »</b>, puis, dans le menu de gauche, rendez-vous dans l'onglet <b>« Date de départ »</b>.\n\n<b>Que faire si la carrière longue n'est pas reconnue ?</b>\nSi le logiciel ne retient pas la date de départ envisagée, je vous invite à vérifier le nombre de trimestres cotisés et réputés cotisés.\n\nEn effet, tous les trimestres ne se valent pas :\n• Le chômage est limité à 4 trimestres\n• Le service militaire à 4 trimestres\n• L'AVPF également dans certaines limites\n\nCes règles peuvent expliquer pourquoi une carrière longue n'est pas automatiquement reconnue.`,
    Q14: `Par défaut, les majorations pour enfants sont affectées au régime général.\n\n<b>Régimes spéciaux :</b>\nToutefois, lorsque votre cliente a cotisé à la fois au régime général et à un régime spécial (fonction publique ou CRPCEN), ce sont en principe ces régimes spéciaux qui accordent les majorations de trimestres, si les conditions sont remplies.\n\n<b>Pour information - Régime des fonctionnaires :</b>\n• Bonification de 4 trimestres par enfant né avant 2004\n• Majoration de 2 trimestres pour l'enfant né à compter de 2004\n\n<b>Ajustement manuel :</b>\nSi nécessaire, vous pouvez ajuster manuellement la répartition des majorations entre les différents régimes afin de refléter au mieux la situation réelle de votre client.`,
    Q15: `Les situations de départ anticipé pour inaptitude, invalidité ou handicap ne sont pas prises en compte automatiquement par le logiciel.\n\n<b>Simulation manuelle :</b>\nEn revanche, si vous pensez que votre client peut y prétendre, il est possible de le simuler manuellement en ajustant certains paramètres.\n\n<b>Procédure :</b>\nPour cela, cliquez sur le bouton rouge <b>« Paramétrer la retraite »</b>, puis rendez-vous dans le menu de gauche sur les onglets <b>« Date de départ »</b> et <b>« Taux plein »</b> afin de forcer l'âge d'ouverture des droits et le taux plein.\n\n<b>Important :</b>\nCes réglages permettent de tester un scénario spécifique, en dehors du calcul automatique. Je vous invite simplement à bien prendre connaissance des informations affichées dans les différents onglets avant de valider.`,

    // Calcul des droits et trimestres
    Q16: `Pour obtenir le détail des calculs, le plus simple est d'éditer l'étude au format Word.\n\nVous y trouverez, dans les annexes, le détail du calcul des pensions, régime par régime, ce qui permet de comprendre précisément comment les montants ont été déterminés.`,
    Q17: `Pour accéder au tableau du RAM, cliquez sur le bouton bleu <b>« Détails des calculs »</b>, situé en bas à droite de l'écran. Une fois la fenêtre ouverte, cliquez dans le menu de gauche sur l'onglet <b>« RAM »</b>. Vous accédez alors à un tableau qui détaille, année par année, la manière dont les revenus sont pris en compte pour le calcul du revenu annuel moyen.\n\n<b>Détail des colonnes du tableau :</b>\n\n<b>Année</b>\nL'année civile concernée.\n\n<b>Revenus</b>\nLe revenu tel qu'il apparaît sur le relevé, dans la monnaie d'origine (par exemple en francs).\n\n<b>Revenus (€)</b>\nLe revenu converti en euros, utilisé pour le calcul.\n\n<b>PASS</b>\nLe plafond annuel de la Sécurité sociale applicable à l'année concernée. Les revenus ne peuvent pas être retenus au-delà de ce plafond.\n\n<b>Revenus pris en compte</b>\nLe revenu effectivement retenu pour le calcul, après application éventuelle du plafond PASS.\n\n<b>Coefficient</b>\nLe coefficient de revalorisation appliqué afin d'actualiser les revenus anciens.\n\n<b>Revenus revalorisés</b>\nLe revenu pris en compte après revalorisation, utilisé dans le calcul final.\n\n<b>Meilleurs revenus</b>\nIndique si l'année fait partie des années retenues pour le calcul du revenu annuel moyen.`,
    Q18: `Le logiciel ne calcule pas automatiquement la majoration enfants sur le régime AGIRC-ARRCO, et c'est tout à fait normal.\n\n<b>Pourquoi ce choix ?</b>\nEn effet, le relevé de situation individuelle (RIS) ne permet pas de distinguer précisément les points AGIRC des points ARRCO. Or, les règles de majoration varient selon le régime et la date d'acquisition des points.\n\nPour éviter toute approximation, le logiciel intègre par défaut les points acquis à compter de 2019 sans lancer automatiquement le calcul de la majoration enfants.\n\n<b>Activer manuellement la majoration :</b>\nSi vous souhaitez malgré tout intégrer cette majoration, notamment parce que vous disposez d'un ancien relevé de points AGIRC-ARRCO, vous pouvez l'activer manuellement.\n\nPour cela, cliquez sur le bouton rouge <b>« Paramétrer la retraite »</b>, puis rendez-vous dans le menu de gauche sur l'onglet <b>« Majorations »</b> afin d'activer le calcul.`,
    Q19: `Oui, le logiciel prend bien en compte la minoration spécifique sur les points en TC (Tranche C), pour les points acquis avant 2016.\n\n<b>Intégration des points :</b>\nCes points sont généralement intégrés automatiquement lors de l'import du RIS et sont consultables dans l'onglet <b>« Points »</b>.\n\n<b>À savoir :</b>\nIl est normal que les points AGIRC acquis avant 2016 apparaissent dans un premier temps avant conversion. La conversion est ensuite appliquée automatiquement pour le calcul final de la pension.\n\nVous n'avez donc pas d'action particulière à effectuer, le logiciel gère cette minoration et cette conversion de manière transparente.`,
    Q20: `Si les trimestres n'évoluent pas dans la projection sur 5 ans, cela peut s'expliquer par deux situations :\n\n<b>1. Absence de période projetée</b>\nAucune période projetée n'a encore été ajoutée.\n\n<b>2. Configuration de la période projetée</b>\nUne période projetée est bien présente, mais elle est configurée avec une date de fin personnalisée, probablement antérieure à la date de départ en retraite.\n\n<b>Solution :</b>\nPour que des trimestres supplémentaires soient validés, la période projetée doit couvrir la durée jusqu'à la date de départ en retraite.\n\nUne fois ce paramétrage ajusté, les trimestres pourront alors évoluer dans la projection.`,
    Q21: `Si le logiciel valide plus de 4 trimestres par an dans la projection sur 5 ans, c'est tout à fait normal.\n\n<b>Explication :</b>\nCela s'explique par un dispositif spécifique de majoration de trimestres, applicable lorsque le départ en retraite intervient au-delà de l'âge du taux plein.\n\nCe mécanisme permet de majorer la durée d'assurance, ce qui peut conduire, dans le cadre de la projection, à valider plus de quatre trimestres sur une même année.\n\n<b>Pour aller plus loin :</b>\nCe dispositif est décrit en détail dans le Mémento de la protection sociale, fiche Régime général, disponible sur l'extranet.`,

    // Conseils
    Q22: `Pour une profession libérale, il est fortement conseillé de positionner la date de départ en retraite au premier jour d'un trimestre civil.\n\n<b>Pourquoi cette règle ?</b>\nEn effet, la quasi-totalité des régimes de professions libérales imposent une liquidation des droits uniquement au premier jour d'un trimestre civil.\n\n<b>Avantage :</b>\nParamétrer la date de départ de cette manière permet donc d'obtenir une simulation plus réaliste et conforme aux règles applicables.`,
    Q23: `Pour ajouter un contrat d'épargne retraite à l'étude, cliquez sur le bouton rouge <b>« Paramétrer la retraite »</b>.\n\nUne fois dans le menu de paramétrage, rendez-vous dans le menu de gauche, onglet <b>« Contrats »</b>, afin de saisir les informations du contrat.`,
    Q24: `Le calcul des pensions retraite dans le cadre des règlements européens ou d'un accord de sécurité sociale avec un pays étranger n'est pas pris en charge automatiquement dans MAKO.\n\n<b>Pourquoi ?</b>\nCela s'explique par la grande diversité des règles applicables selon les pays et les accords concernés.\n\n<b>Solution manuelle :</b>\nEn revanche, pour tenir compte des trimestres accomplis à l'étranger, vous pouvez les ajouter manuellement dans la carrière.\n\nPour cela, il suffit d'ajouter une période en sélectionnant le type <b>« Trimestres spécifiques »</b>.\n\nDans ce cadre, le logiciel retient 1 trimestre pour 90 jours déclarés.`,
  },
};

// Provider component
export const CarriereEventsProvider = ({ children }) => {
  const authContext = useContext(AuthContext);

  // Get initial welcome message with replaced prenom
  const getInitialMessage = useCallback(() => {
    let text = EVENT_MESSAGES.BIENVENUE.INIT;
    if (text.includes("{prenom}")) {
      const prenom = authContext?.utilisateur_reel?.prenom || "Utilisateur";
      text = text.replace("{prenom}", prenom);
    }
    return text;
  }, [authContext]);

  const [state, setState] = useState({
    messages: [
      {
        id: Date.now(),
        timestamp: new Date(),
        type: "bot",
        context: "BIENVENUE",
        text: getInitialMessage(),
      },
    ],
    unreadCount: 0,
    chatbotVisible: false,
    isInModal: false,
  });

  // Generate dynamic parameter message based on activity type
  const getParametresMessage = useCallback((activite) => {
    const baseMessage = `Section Paramètres : Configurez les paramètres spécifiques selon le régime de retraite.\n\n`;

    // Messages spécifiques par type d'activité
    const messages = {
      SalarieNonCadre: `<b>👔 Taux d'activité :</b> 
      Représente le temps de travail par rapport au temps plein.\n  → <b>100%</b> = Temps plein 
      → <b>50%</b> = Mi-temps 
      <i>Ce taux influence directement la proratisation du PASS.</i>

      <b>💳 Cotisations AGIRC-ARRCO :</b> 
      Taux de cotisations salariales et patronales sur Tranche 1 et Tranche 2. Les taux par défaut correspondent aux taux légaux en vigueur.`,
      SalarieCadre: `<b>👔 Taux d'activité :</b> 
      Représente le temps de travail par rapport au temps plein.\n  → <b>100%</b> = Temps plein 
      → <b>50%</b> = Mi-temps 
      <i>Ce taux influence directement la proratisation du PASS.</i>

      <b>💳 Cotisations AGIRC-ARRCO :</b> 
      Taux de cotisations salariales et patronales sur Tranche 1 et Tranche 2. Les taux par défaut correspondent aux taux légaux en vigueur.`,
      SalarieNonCadreCFE: `<b>👔 Taux d'activité :</b> 
       Représente le temps de travail par rapport au temps plein.
      → <b>100%</b> = Temps plein 
      → <b>50%</b> = Mi-temps 
      <i>Ce taux influence directement la proratisation du PASS.</i>

      <b>Affiliation AGIRC-ARRCO :</b> 
      Précisez si le salarié détaché à l'étranger cotise à l'AGIRC-ARRCO

       <b>💳 Cotisations AGIRC-ARRCO : </b> 
       Si affilié, indiquez les taux de cotisations sur les deux tranches`,
      SalarieCadreCFE: `<b>👔 Taux d'activité :</b> 
       Représente le temps de travail par rapport au temps plein.
      → <b>100%</b> = Temps plein 
      → <b>50%</b> = Mi-temps 
      <i>Ce taux influence directement la proratisation du PASS.</i>

      <b>Affiliation AGIRC-ARRCO :</b> 
      Précisez si le salarié détaché à l'étranger cotise à l'AGIRC-ARRCO

       <b>💳 Cotisations AGIRC-ARRCO : </b> 
       Si affilié, indiquez les taux de cotisations sur les deux tranches`,
      SalarieIrcantec: `<b>👔 Taux d'activité :</b> 
       Représente le temps de travail par rapport au temps plein.
      → <b>100%</b> = Temps plein 
      → <b>50%</b> = Mi-temps 
      <i>Ce taux influence directement la proratisation du PASS.</i>
      
      <b>Statut :</b>
      Le choix du statut impacte la cotisation au régime de retraite complémentaire IRCANTEC.
      
      Exemples sur le praticien hospitalier :
      • Temps plein sans activité libérale : 100% des émoluments
      • Temps plein avec activité libérale : 2/3 des émoluments
      • Temps partiel : 100% des émoluments
      • Contractuel temps plein : 100% des émoluments
      • Contractuel temps partiel : 70% des émoluments
      
      <i>Note : Certaines catégories (ex: agents recenseurs) ont une assiette forfaitaire ou spécifique.</i>
      
      <b>💳 Cotisations IRCANTEC :</b> 
      Taux théoriques de cotisations salariales et patronales sur Tranche A et Tranche B`,
      FonctionPublique: `<b>👔 Taux d'activité :</b> 
      Représente le temps de travail par rapport au temps plein.\n  → <b>100%</b> = Temps plein 
      → <b>50%</b> = Mi-temps 
      <i>Ce taux influence la durée de service effective.</i>
      
      <b>Catégorie :</b> 
     Le choix de la catégorie impacte l'âge de départ en retraite.
      Les fonctionnaires de catégorie active occupent un emploi qui présente un risque particulier ou des fatigues exceptionnelles. 
De manière générale, un fonctionnaire de catégorie active peut partir à la retraite à partir de 57 ans s'il a occupé un emploi de catégorie active pendant au moins 17 ans. Mais pour certains emplois, les conditions sont différentes.

      <b>Limite d'âge personnalisée :</b> 
      La limite d’âge est la date à laquelle un fonctionnaire est obligé de cesser son activité et de partir à la retraite, sauf dérogations spécifiques.Il est conseillé de ne pas modifier ce paramètre.
      
      <b>Bonification :</b> 
      Les bonifications sont des trimestres supplémentaires qui viennent s'ajouter gratuitement aux années de services effectifs afin d'augmenter le montant de la pension. Elles sont octroyées sous conditions, au titre des services accomplis par le fonctionnaire (ex : Bonification pour exécution d'un service aérien ou sous-marin).
      
      <b>MDA fonction publique hospitalière :</b> 
      Sous certaines conditions, les fonctionnaires hospitaliers peuvent bénéficier d’une majoration de trimestres. Elle est fixée à 4 trimestres par période de 10 années de services effectifs (c’est-à-dire les services pris en compte en liquidation) effectués en catégorie active ou sédentaire, dans la fonction publique hospitalière, territoriale et de l’Etat.
      
      <b>Indice majoré de fin de carrière :</b>
      Le traitement indiciaire retenu est celui du dernier emploi, grade et échelon détenus  pendant au moins les 6 derniers mois valables pour la retraite ou à défaut, celui afférent à l’emploi, grade, classe et échelon antérieurement occupés d’une manière effective. Le renseigner n'est pas obligatoire. Si vous faites le choix de ne pas le faire, le logiciel calculera la retraite sur la base du dernier revenu renseigné dans l'onglet "revenus".
      
      <b>Assiette RAFP :</b> 
      Base de calcul du régime additionnel (en % du traitement brut ou forfaitaire)`,

      TrimSpecifique: `<b>✅ Validation des trimestres :</b>
      - <b>Taux de pension :</b> Les trimestres comptent uniquement pour le calcul du taux de liquidation
      - <b>Taux + durée d'assurance :</b> Les trimestres comptent à la fois pour le taux ET pour la durée d'affiliation au régime général`,
      Cavamac: `<b>💼 Commissions brutes :</b> 
      Indiquez année par année les commissions brutes perçues. Ces montants servent de base au calcul des cotisations et des droits à la retraite complémentaire.`,
      Carpv: `<b>💊 Classe retraite :</b> 
      En fonction de son revenu, le vétérinaire est affecté à l’une des 3 classes (B, C, D) prévue par le régime complémentaire.  La cotisation due pour l’année N est calculée sur la base du revenu de l’année N-2 sans régularisation ultérieure.`,
      Cnbf: `<b>⚖️ Classe retraite :</b> 
      Le choix de la classe dépend du choix exprimé conjointement par l’avocat avant le 31 janvier de l’année d’exigibilité.`,
      CnbfSalarie: `<b>⚖️ Classe retraite :</b> 
      Le choix de la classe dépend du choix exprimé conjointement par l’avocat salarié et son employeur avant le 1er janvier de l’année d’exigibilité.`,
      Cavec: `<b>💼 Classe retraite :</b> 
     En fonction de son revenu, l’expert-comptable est affecté à l’une des 9 classes prévue par le régime complémentaire. L’adhérent a toutefois la faculté d’opter, chaque année, pour la classe immédiatement supérieure à celle ainsi ajustée. Cette option est reconduite tacitement chaque année.  En fonction de da classe de cotisation, l’expert-comptable acquière un nombre de points déterminé.`,
      CavecSalarie: `<b>👔 Taux d'activité :</b> 
       Représente le temps de travail par rapport au temps plein.
      → <b>100%</b> = Temps plein 
      → <b>50%</b> = Mi-temps 
      <i>Ce taux influence directement la proratisation du PASS.</i>

      <b>💳 Cotisations AGIRC-ARRCO :</b> 
      Taux de cotisations salariales et patronales sur Tranche 1 et Tranche 2. Les taux par défaut correspondent aux taux légaux en vigueur.

      <b>Classe retraite CAVEC :</b>
       les experts comptables salariés sont tenus de cotiser en classe C. Les employeurs ont la faculté, chaque année, d’opter pour leur salarié, dans la classe immédiatement supérieure (classe D). Cette option est reconduite tacitement chaque année.`,
      Crn: `<b>📝 Classe retraite :</b> 
      La section B comporte huit classes de cotisations ouvrant chacune droit annuellement à un nombre de points de retraite. Les cotisations sont assises sur la moyenne des produits de base de l’office des exercices (n-4 à n-2).
      
      <b>Prestation de serment avant 2014 :</b> 
      Un coefficient de majoration de 115% est appliqué à la cotisation de la section B pour les notaires ayant prêté serment à partir de 2014.
      
       <b>🏛️ Régime de Colmar et Metz :</b> 
       Les notaires relevant des Cours d’Appel de Colmar et Metz bénéficient d’un régime spécial financé par une cotisation spécifique. 
Celle-ci leur procure des droits supplémentaires.
       
       <b>📊 Moyenne des produits :</b> 
        Ces produits s’entendent du total des émoluments encaissés ou non, au profit du Notaire pour tous les actes reçus et les 
        services rendus entre le 1er janvier et le 31 décembre de chaque année déduction faite : 
          &nbsp;&nbsp;&nbsp;&nbsp;-  De la taxe d’apprentissage 
          &nbsp;&nbsp;&nbsp;&nbsp;-  De la participation à la formation continue 
          &nbsp;&nbsp;&nbsp;&nbsp;-  De la participation à l’effort de construction 
          &nbsp;&nbsp;&nbsp;&nbsp;-  De la rémunération du personnel 
          &nbsp;&nbsp;&nbsp;&nbsp;-  De la rémunération des Notaires salariés 
          &nbsp;&nbsp;&nbsp;&nbsp;-  De la rémunération des Notaires associés en SEL 
          &nbsp;&nbsp;&nbsp;&nbsp;-  Des charges sociales pour le personnel y compris les cotisations de la CRPCEN 
          &nbsp;&nbsp;&nbsp;&nbsp;-  Des autres charges sociales et de personnels. 
        Une déduction supplémentaire de 15% est appliquée sur le résultat.`,
      RsiCommercant: `<b>🏪 Affiliation RAVGDT :</b> 
      Le régime d'allocations viagères des gérants de Débits de Tabac (RAVGDT) est un régime obligatoire dédié aux buralistes.
      
      <b>Remises brutes corrigées :</b> 
      Un débitant de tabac est rémunéré au moyen d'une remise brute accordée par les fournisseurs agréés sur la vente des produits du tabac.
      La remise brute est constituée de la remise nette, du droit de licence et de la cotisation au RAVGDT.`,
      Chomage: `<b>💳 Cotisations AGIRC-ARRCO :</b> 
      Seules les périodes de chômage indemnisé permettent l’attribution de points de retraite.
      
      <b>Salaire journalier de référence :</b> 
      Il correspond, en principe, au salaire perçu par l’assuré au cours de ses 12 derniers mois de travail, divisé par 365 jours. Il est calculé par Pôle emploi afin de déterminer le montant de l’indemnisation chômage de l’assuré. Le salaire journalier de référence (SJR) est également utilisé par la caisse de retraite de l’assuré pour fixer l’assiette fictive de ses cotisations.
     
      <b>ℹ️ Informations :</b>
      Les points de retraite sont attribués pour chaque jour indemnisé par Pôle emploi. Les périodes de carence  et de différé d’indemnisation, déterminées par Pôle emploi, ne permettent donc pas l’acquisition de points de retraite complémentaire.
      `,
      Maladie: `<b>🏥 Maladie et points AGIRC-ARRCO :</b>
      En cas d’arrêt de travail supérieur à 60 jours consécutifs pour maladie, accident du travail ou invalidité, l’assuré peut obtenir des points de retraite sans contrepartie de cotisations, sous certaines conditions.
      Les points de retraite attribués pendant l’arrêt de travail de l’assuré sont calculés sur la base de ceux obtenus au cours de l’année précédant celle de l’interruption de travail. Cette année n-1 est considérée comme la période de référence.
      La caisse de retraite attribue des points pour chaque jour indemnisé. Pour ce faire, elle calcule une moyenne journalière en divisant le nombre de points acquis par le nombre de jours composant la période de référence.`,

      Invalidite: `<b>🏥 Invalidité et points AGIRC-ARRCO :</b>
      En cas d’arrêt de travail supérieur à 60 jours consécutifs pour maladie, accident du travail ou invalidité, l’assuré peut obtenir des points de retraite sans contrepartie de cotisations, sous certaines conditions.
      Les points de retraite attribués pendant l’arrêt de travail de l’assuré sont calculés sur la base de ceux obtenus au cours de l’année précédant celle de l’interruption de travail. Cette année n-1 est considérée comme la période de référence.
      La caisse de retraite attribue des points pour chaque jour indemnisé. Pour ce faire, elle calcule une moyenne journalière en divisant le nombre de points acquis par le nombre de jours composant la période de référence.`,

      Carcdsf: `<b>🦷 Conventionné :</b> 
      Les chirurgiens dentistes conventionnés cotisent au régime de prestations complémentaires de vieillesse. Laissez ce paramètre sur "Oui" déclenche un calcul de points au régime PCV.
      
      <b>Taux de prise en charge CPAM :</b> 
      Le taux correspond au ratio des revenus conventionnés sur la totalité des revenus. Il est utilisé pour déterminer la part des revenus soumis à cotisation au régime de prestations complémentaires de vieillesse (PCV)`,
      Carcdsf2: `<b>👶 Conventionné :</b> 
      Les sages-femmes conventionnées cotisent au régime de prestations complémentaires de vieillesse. Laissez ce paramètre sur "Oui" déclenche un calcul de points au régime PCV.
      
      <b>Taux de prise en charge CPAM :</b>
       Le taux correspond au ratio des revenus conventionnés sur la totalité des revenus. Il est utilisé pour déterminer la part des revenus soumis à cotisation au régime de prestations complémentaires de vieillesse (PCV)`,
      Carpimko: `<b>💉 Conventionné :</b> 
     Les auxiliaires médicaux conventionnés cotisent au régime de prestations complémentaires de vieillesse. Laissez ce paramètre sur "Oui" déclenche un calcul de points au régime PCV.
     
     <b>Taux de prise en charge CPAM :</b> 
      Le taux correspond au ratio des revenus conventionnés sur la totalité des revenus. Il est utilisé pour déterminer la part des revenus soumis à cotisation au régime de prestations complémentaires de vieillesse (PCV)`,
      Cavp: `<b>🔬 Biologiste conventionné :</b> 
      Les biologistes conventionnés cotisent au régime de prestations complémentaires de vieillesse. Laissez ce paramètre sur "Oui" déclenche un calcul de points au régime PCV.
     
      <b>📋 Classe retraite :</b> 
      Le régime complémentaire comporte onze classes de cotisation (Classes 3 à 13). Chaque classe de cotisation comprend une fraction de cotisation gérée en répartition, le solde de la cotisation étant géré en capitalisation.

      <b>💰 Paramètres du régime par capitalisation :</b> 
      Par défaut, le logiciel utilise les paramètres standards du régime par capitalisation. Si vous disposez d'informations spécifiques sur le régime de votre client, vous pouvez ajuster les paramètres.`,
      Carmf: `<b>👨‍⚕️ Conventionné :</b> 
      Les médecins conventionnés cotisent au régime d'allocations supplémentaires de vieillesse. Choisir un secteur déclenche un calcul de points au régime ASV. Le choix du secteur n'a pas d'incidence sur le calcul retraite effectué par le logiciel.
      
      <b>Taux de prise en charge CPAM :</b> 
      Le taux correspond au ratio des revenus conventionnés sur la totalité des revenus. Il est utilisé pour déterminer la part des revenus soumis à cotisation au régime ASV.`,
    };

    // Activités liées (regrouper les variantes)
    const activitesGroupe = {
      SalarieNonCadreAgricole: "SalarieNonCadre",
      SalarieCadreAgricole: "SalarieCadre",
    };

    // Conseils spécifiques par type d'activité
    const conseils = {
      SalarieNonCadre: `Les paramètres par défaut sont généralement adaptés aux situations les plus courantes. <b>Attention</b>, les fiches de paie intègrent généralement les cotisations CET et CEG dans les taux AGIRC-ARRCO.`,
      SalarieCadre: `Les paramètres par défaut sont généralement adaptés aux situations les plus courantes. <b>Attention</b>, les fiches de paie intègrent généralement les cotisations CET et CEG dans les taux AGIRC-ARRCO.`,
      SalarieNonCadreCFE: `Les paramètres par défaut sont généralement adaptés aux situations les plus courantes. <b>Attention</b>, les fiches de paie intègrent généralement les cotisations CET et CEG dans les taux AGIRC-ARRCO.`,
      SalarieCadreCFE: `Les paramètres par défaut sont généralement adaptés aux situations les plus courantes. <b>Attention</b>, les fiches de paie intègrent généralement les cotisations CET et CEG dans les taux AGIRC-ARRCO.`,
      SalarieIrcantec: `Les paramètres par défaut sont généralement adaptés aux situations les plus courantes. Ne les modifiez que si vous disposez d'informations précises sur la situation de votre client.`,
      FonctionPublique: `L'assiette de la RAFP peut être déterminée sur une fiche de paie. Si vous choississez une assiette en pourcentage du traitement brut, il est indispensable de saisir le traitement brut dans l'onglet "Revenus" pour que le logiciel calcule des points.`,
      TrimSpecifique: `Précisez bien la nature des trimestres pour vérifier l'éligibilité au dispositif de carrière longue.`,
      Cavamac: `La cotisation due pour l’année N est calculée sur les commissions et rémunérations brutes de l’année N-1 sans régularisation ultérieure. Consultez les appels de cotisations CAVAMAC déterminer le montant.`,
      Carpv: `Vous pouvez déterminer si votre client est conventionné en vérifiant si le RIS contient des points au régime supplémentaire de vieillesse. Vous pouvez également vérifier la part des revenus conventionnés sur un appel de cotisation.`,
      Cnbf: `Consultez les appels de cotisations de la CNBF pour vérifier la classe de cotisation.`,
      CnbfSalarie: `Munissez vous d'une fiche de paie pour vérifier la classe de cotisation.`,
      Cavec: `La classe de cotisation peut être facilement déterminée sur le RIS au regard du nombre de points acquis chaque année.`,
      CavecSalarie: `Les paramètres par défaut sont généralement adaptés. <b>Attention</b>, les fiches de paie intègrent généralement les cotisations CET et CEG dans les taux AGIRC-ARRCO.`,
      Crn: `Consultez les appels de cotisations de la CRN pour vérifier la classe de cotisation et la moyenne des produits de l'étude.`,
      RsiCommercant: `Consultez les appels de cotisations RAVGDT déterminer le montant des remises brutes corrigées.`,
      Chomage: `Vérifiez si la période ouvre droit à des points AGIRC-ARRCO selon la nature du chômage.`,
      Maladie: `Les points de retraite sont calculés sur la base des droits obtenus au cours de l’année n-1 qui précède l’année n pendant laquelle l’interruption de travail intervient. La caisse de retraite calcule la moyenne journalière des points de retraite en divisant le nombre de points obtenus au cours de l’année n-1 par 365.
      Exemple : Si le nombre de points de l’année N-1 est de 100, la moyenne journalière à saisir est de 0,2739 (100/365).
      En cas de période incomplète, la moyenne journalière est calculée en divisant le nombre de points de n-1 par le nombre de jours pendant lesquels ces points ont été acquis.
      Exemple : Si le nombre de points de l’année N-1 est de 100 et que la période N-1 correspond à 180 jours, le nombre de points à saisir est de 0,555 (100/180).`,
      Invalidite: `Les points de retraite sont calculés sur la base des droits obtenus au cours de l’année n-1 qui précède l’année n pendant laquelle l’interruption de travail intervient. La caisse de retraite calcule la moyenne journalière des points de retraite en divisant le nombre de points obtenus au cours de l’année n-1 par 365.
      Exemple : Si le nombre de points de l’année N-1 est de 100, la moyenne journalière à saisir est de 0,2739 (100/365).
      En cas de période incomplète, la moyenne journalière est calculée en divisant le nombre de points de n-1 par le nombre de jours pendant lesquels ces points ont été acquis.
      Exemple : Si le nombre de points de l’année N-1 est de 100 et que la période N-1 correspond à 180 jours, le nombre de points à saisir est de 0,555 (100/180).`,
      Carcdsf: `Vous pouvez déterminer si votre client est conventionné en vérifiant si le RIS contient des points au régime supplémentaire de vieillesse. Vous pouvez également vérifier la part des revenus conventionnés sur un appel de cotisation.`,
      Carcdsf2: `Vous pouvez déterminer si votre client est conventionné en vérifiant si le RIS contient des points au régime supplémentaire de vieillesse. Vous pouvez également vérifier la part des revenus conventionnés sur un appel de cotisation.`,
      Carpimko: `Vous pouvez déterminer si votre client est conventionné en vérifiant si le RIS contient des points au régime supplémentaire de vieillesse. Vous pouvez également vérifier la part des revenus conventionnés sur un appel de cotisation.`,
      Carmf: `Vous pouvez déterminer si votre client est conventionné en vérifiant si le RIS contient des points au régime supplémentaire de vieillesse. Vous pouvez également vérifier la part des revenus conventionnés sur un appel de cotisation.`,
      Cavp: `La classe de cotisation peut être facilement déterminée sur le RIS au regard du nombre de points acquis chaque année.`,
    };

    const activiteKey = activitesGroupe[activite] || activite;
    const specificMessage = messages[activiteKey];
    const conseilSpecifique = conseils[activiteKey] || `En cas de doute, conservez les valeurs par défaut proposées par le logiciel.`;

    if (specificMessage) {
      return (
        baseMessage +
        specificMessage +
        `\n\n<b>💡 Conseil :</b> 
        ${conseilSpecifique}`
      );
    }

    // Message par défaut si l'activité n'est pas reconnue
    return (
      baseMessage +
      `<b>ℹ️ Information :</b> Les paramètres disponibles dépendent du type d'activité sélectionné. Configurez les champs selon les informations dont vous disposez.\n\n<b>💡 Conseil :</b> En cas de doute, conservez les valeurs par défaut proposées par le logiciel.`
    );
  }, []);

  // Track an event and add corresponding message
  const trackEvent = useCallback(
    (context, action, data = null) => {
      let messageText = EVENT_MESSAGES[context]?.[action];

      // Handle dynamic parametres message
      if (context === "PERIODE" && action === "TAB_PARAMETRES" && data?.activite) {
        messageText = getParametresMessage(data.activite);
      }

      if (messageText) {
        // Replace {prenom} with actual user's first name
        if (messageText.includes("{prenom}")) {
          const prenom = authContext?.utilisateur_reel?.prenom || "Utilisateur";
          messageText = messageText.replace("{prenom}", prenom);
        }

        const newMessage = {
          id: Date.now() + Math.random(), // Ensure unique ID
          timestamp: new Date(),
          type: "bot",
          context: context,
          text: messageText,
          data: data,
        };

        setState((prev) => {
          // When starting a new system event (RIS, PERIODE, PARAM), show only BIENVENUE + new message
          const isSystemEvent = ["RIS", "PERIODE", "PARAM"].includes(context);
          let filteredMessages = prev.messages;

          if (isSystemEvent) {
            // Keep only the BIENVENUE message, remove all other messages
            filteredMessages = prev.messages.filter((m) => m.context === "BIENVENUE");
          }

          return {
            ...prev,
            messages: [...filteredMessages, newMessage],
            unreadCount: prev.chatbotVisible ? prev.unreadCount : prev.unreadCount + 1,
          };
        });
      }
    },
    [authContext]
  );

  // Return to menu (called when modal is validated)
  const returnToMenu = useCallback(() => {
    setState((prev) => {
      // Keep only the BIENVENUE message
      const welcomeMessage = prev.messages.find((m) => m.context === "BIENVENUE");
      return {
        ...prev,
        messages: welcomeMessage ? [welcomeMessage] : prev.messages,
      };
    });
  }, []);

  // Add a custom message
  const addMessage = useCallback((context, text, type = "bot") => {
    const newMessage = {
      id: Date.now() + Math.random(),
      timestamp: new Date(),
      type: type,
      context: context,
      text: text,
    };

    setState((prev) => ({
      ...prev,
      messages: [...prev.messages, newMessage],
      unreadCount: prev.chatbotVisible ? prev.unreadCount : prev.unreadCount + 1,
    }));
  }, []);

  // Toggle chatbot visibility
  const toggleChatbot = useCallback(() => {
    setState((prev) => ({
      ...prev,
      chatbotVisible: !prev.chatbotVisible,
      unreadCount: !prev.chatbotVisible ? 0 : prev.unreadCount, // Reset unread when opening
    }));
  }, []);

  // Reset all messages
  const resetMessages = useCallback(() => {
    setState({
      messages: [
        {
          id: Date.now(),
          timestamp: new Date(),
          type: "bot",
          context: "WELCOME",
          text: getInitialMessage(),
        },
      ],
      unreadCount: 0,
      chatbotVisible: true, // Keep chatbot open after reset
    });
  }, [getInitialMessage]);

  // Mark all messages as read
  const markAllRead = useCallback(() => {
    setState((prev) => ({
      ...prev,
      unreadCount: 0,
    }));
  }, []);

  // Set modal state
  const setIsInModal = useCallback((isInModal) => {
    setState((prev) => ({
      ...prev,
      isInModal,
    }));
  }, []);

  // Handle question click - adds the question and its answer to messages
  const handleQuestionClick = useCallback((questionId, questionText) => {
    // Add user's question
    const userMessage = {
      id: Date.now() + Math.random(),
      timestamp: new Date(),
      type: "user",
      context: "QUESTION",
      text: questionText,
    };

    // Get the answer
    const answerText = EVENT_MESSAGES.QUESTIONS[questionId];
    const botMessage = {
      id: Date.now() + Math.random() + 0.1,
      timestamp: new Date(),
      type: "bot",
      context: "REPONSE",
      text: answerText || "Désolé, je n'ai pas trouvé de réponse à cette question.",
      questionId: questionId,
    };

    setState((prev) => {
      // Keep only the BIENVENUE message and add the new question/answer
      const welcomeMessage = prev.messages.find((m) => m.context === "BIENVENUE");
      const newMessages = welcomeMessage ? [welcomeMessage, userMessage, botMessage] : [userMessage, botMessage];

      return {
        ...prev,
        messages: newMessages,
        unreadCount: prev.chatbotVisible ? prev.unreadCount : prev.unreadCount + 2,
      };
    });
  }, []);

  const contextValue = {
    messages: state.messages,
    unreadCount: state.unreadCount,
    chatbotVisible: state.chatbotVisible,
    isInModal: state.isInModal,
    categories: EVENT_MESSAGES.BIENVENUE.CATEGORIES,
    questions: EVENT_MESSAGES.QUESTIONS,
    trackEvent,
    addMessage,
    toggleChatbot,
    resetMessages,
    markAllRead,
    handleQuestionClick,
    returnToMenu,
    setIsInModal,
  };

  return <CarriereEventsContext.Provider value={contextValue}>{children}</CarriereEventsContext.Provider>;
};
